import { Store } from "vuex";
import { ApiStatus } from "@/store/general.types";
import { citationApi } from "@/api/citation.api"
import { CitationDuplicateCountPayload, CitationPrimaryReferencePayload, CitationScreeningCountPayload, DuplicateCitationState, UpdateCitationStatusPayload } from "./duplicate-citation.types";
import { DuplicateUndoStackDetailsPayLoad, DuplicationUndoScreen, GetDuplicateCitationPayLoad, MarkCitationAsDuplicatePayload } from "../citation/citation.types";

export const DuplicateCitationActions = {
    async getDuplicateCitation(store: Store<DuplicateCitationState>, citationPayload: GetDuplicateCitationPayLoad) {
        try {
            store.commit('GET_DUPLICATE_CITATION', ApiStatus.LOADING);
            // JIRA: SUMDEV-2964 - Using new cluster-based API
            const { data } = await citationApi.getDuplicateCitationV2(citationPayload);
            store.commit('GET_DUPLICATE_CITATION', { data })
        } catch (error) {
            store.commit('GET_DUPLICATE_CITATION', { error })
        }
    },
    async getPossibleDuplicateCitations(store: Store<DuplicateCitationState>, citationPayload: GetDuplicateCitationPayLoad) {
        try {
            store.commit('GET_POSSIBLE_DUPLICATE_CITATIONS', ApiStatus.LOADING);
            // JIRA: SUMDEV-2964 - Using new cluster-based API
            const { data } = await citationApi.getPossibleDuplicateCitationsV2(citationPayload);
            store.commit('GET_POSSIBLE_DUPLICATE_CITATIONS', { data })
        } catch (error) {
            store.commit('GET_POSSIBLE_DUPLICATE_CITATIONS', { error })
        }
    },
    async markCitationAsDuplicate(store: Store<DuplicateCitationState>, { projectId, citationIds, status, stage }: MarkCitationAsDuplicatePayload) {
        try {
            store.commit('MARK_CITATION_AS_DUPLICATE', ApiStatus.LOADING);
            const { data } = await citationApi.markCitationAsDuplicate({ projectId, citationIds, status, stage });
            store.commit('MARK_CITATION_AS_DUPLICATE', { data });
        } catch (error) {
            store.commit('MARK_CITATION_AS_DUPLICATE', { error })

        }
    },
    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Mark citations as manually marked exact duplicates
     * Removes citations from their current clusters and marks them as manually marked duplicates
     */
    async markCitationAsManuallyMarkedDuplicate({ commit }: any, payload: { projectId: number; citationIds: number[]; reason?: string; sourceStage?: string }) {
        try {
            commit('MARK_CITATION_AS_MANUALLY_MARKED_DUPLICATE', ApiStatus.LOADING);
            const { data } = await citationApi.markCitationAsManuallyMarkedDuplicate(payload.projectId, payload.citationIds, payload.reason, payload.sourceStage);
            commit('MARK_CITATION_AS_MANUALLY_MARKED_DUPLICATE', { data });
        } catch (error) {
            commit('MARK_CITATION_AS_MANUALLY_MARKED_DUPLICATE', { error });
        }
    },
    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Get manually marked duplicate citations using new cluster-based system
     * Returns citations that are marked as manually marked exact duplicates
     */
    async getManuallyMarkedDuplicateCitationsV2(store: Store<DuplicateCitationState>, citationPayload: GetDuplicateCitationPayLoad) {
        try {
            store.commit('GET_MANUALLY_MARKED_DUPLICATE_CITATIONS_V2', ApiStatus.LOADING);
            const { data } = await citationApi.getManuallyMarkedDuplicateCitationsV2(citationPayload);
            store.commit('GET_MANUALLY_MARKED_DUPLICATE_CITATIONS_V2', { data });
        } catch (error) {
            store.commit('GET_MANUALLY_MARKED_DUPLICATE_CITATIONS_V2', { error });
        }
    },
    async updateCitationStatus(store: Store<DuplicateCitationState>, { projectId, citationIds, userId, screeningStage: screeningType, status }: UpdateCitationStatusPayload) {
        try {
            store.commit('UPDATE_CITATION_STATUS', ApiStatus.LOADING);
            const { data } = await citationApi.updateCitationStatus({ projectId, citationIds, userId, screeningStage: screeningType, status })
            store.commit('UPDATE_CITATION_STATUS', { data });
        } catch (error) {
            store.commit('UPDATE_CITATION_STATUS', { error })

        }
    },
    async replacePrimaryReference(store: Store<DuplicateCitationState>, payload: CitationPrimaryReferencePayload) {
        try {
            store.commit('REPLACE_PRIMARY_REFERENCE', ApiStatus.LOADING);
            const { data } = await citationApi.replacePrimaryReference(payload);
            store.commit('REPLACE_PRIMARY_REFERENCE', { data });
        } catch (error) {
            store.commit('REPLACE_PRIMARY_REFERENCE', { error });
        }
    },
    async duplicateCitationCount(store: Store<DuplicateCitationState>, payload: CitationDuplicateCountPayload) {
        try {
            store.commit('DUPLICATE_CITATION_COUNT', ApiStatus.LOADING);
            const { data } = await citationApi.getDuplicateCount(payload);
            store.commit('DUPLICATE_CITATION_COUNT', { data });
        } catch (error) {
            store.commit('DUPLICATE_CITATION_COUNT', { error });
        }
    },
    async getDuplicateCitationCountByFileId(store: Store<DuplicateCitationState>, payload: CitationDuplicateCountPayload) {
        try {
            store.commit('GET_DUPLICATE_CITATION_COUNT_BY_FILE_ID', ApiStatus.LOADING);
            const { data } = await citationApi.getDuplicateCitationCountByFileId(payload);
            store.commit('GET_DUPLICATE_CITATION_COUNT_BY_FILE_ID', { data })
        } catch (error) {
            store.commit('GET_DUPLICATE_CITATION_COUNT_BY_FILE_ID', { error })
        }
    },

    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Mark citations as not duplicate (remove from clusters and make available for screening)
     * Removes citations from their current clusters and marks them as available for screening
     */
    async markCitationAsNotDuplicate({ commit }: any, { projectId, citationIds, reason, sourceStage }: { projectId: number; citationIds: number[]; reason?: string; sourceStage?: string }) {
        try {
            commit('MARK_CITATION_AS_NOT_DUPLICATE', ApiStatus.LOADING);
            const { data } = await citationApi.markCitationAsNotDuplicate(projectId, citationIds, reason, sourceStage);
            commit('MARK_CITATION_AS_NOT_DUPLICATE', { data });
        } catch (error) {
            commit('MARK_CITATION_AS_NOT_DUPLICATE', { error });
            throw error;
        }
    },

    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Replace primary reference in exact duplicate cluster with cluster-based logic
     * Handles dismantling of POSSIBLE clusters if the current primary is also primary in POSSIBLE cluster
     */
    async replacePrimaryReferenceV2({ commit }: any, { projectId, currentPrimaryCitationId, newPrimaryCitationId, clusterId }: { projectId: number; currentPrimaryCitationId: number; newPrimaryCitationId: number; clusterId: string }) {
        try {
            commit('REPLACE_PRIMARY_REFERENCE_V2', ApiStatus.LOADING);
            const { data } = await citationApi.replacePrimaryReferenceV2(projectId, currentPrimaryCitationId, newPrimaryCitationId, clusterId);
            commit('REPLACE_PRIMARY_REFERENCE_V2', { data });
        } catch (error) {
            commit('REPLACE_PRIMARY_REFERENCE_V2', { error });
        }
    },

    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Replace primary reference in possible duplicate cluster with validation logic
     * Validates that new primary is a possible duplicate with all non-primary members
     */
    async replacePrimaryReferencePossibleV2({ commit }: any, { projectId, currentPrimaryCitationId, newPrimaryCitationId, clusterId }: { projectId: number; currentPrimaryCitationId: number; newPrimaryCitationId: number; clusterId: string }) {
        try {
            commit('REPLACE_PRIMARY_REFERENCE_POSSIBLE_V2', ApiStatus.LOADING);
            const { data } = await citationApi.replacePrimaryReferencePossibleV2(projectId, currentPrimaryCitationId, newPrimaryCitationId, clusterId);
            commit('REPLACE_PRIMARY_REFERENCE_POSSIBLE_V2', { data });
        } catch (error) {
            commit('REPLACE_PRIMARY_REFERENCE_POSSIBLE_V2', { error });
        }
    },

    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Undo file import with cluster-based logic
     * Handles EXACT clusters, POSSIBLE clusters, and manually marked duplicates
     */
    async undoFileImportV2({ commit }: any, { projectId, fileId }: { projectId: number; fileId: number }) {
        try {
            commit('UNDO_FILE_IMPORT_V2', ApiStatus.LOADING);
            const { data } = await citationApi.undoFileImportV2(projectId, fileId);
            commit('UNDO_FILE_IMPORT_V2', { data });
        } catch (error) {
            commit('UNDO_FILE_IMPORT_V2', { error });
        }
    },

    async getManuallyMarkedDuplicateCitation(store: Store<DuplicateCitationState>, payload: GetDuplicateCitationPayLoad) {
        try {
            store.commit('GET_MANUALLY_MARKED_DUPLICATE_CITATION', ApiStatus.LOADING);
            const { data } = await citationApi.getManuallyMarkedDuplicateCitations(payload);
            store.commit('GET_MANUALLY_MARKED_DUPLICATE_CITATION', { data });
        } catch (error) {
            store.commit('GET_MANUALLY_MARKED_DUPLICATE_CITATION', { error });
        }
    },
    async getDuplicateCitationGroupCount(store: Store<DuplicateCitationState>, projectId: number) {
        try {
            store.commit('GET_DUPLICATE_CITATION_GROUP_COUNT', ApiStatus.LOADING);
            const { data } = await citationApi.getDuplicateCitationGroupCount(projectId);
            store.commit('GET_DUPLICATE_CITATION_GROUP_COUNT', { data });
        } catch (error) {
            store.commit('GET_DUPLICATE_CITATION_GROUP_COUNT', { error });
        }
    },
    async getRemovedDuplicateUndoStackDetails(store: Store<DuplicateCitationState>, projectId: number) {
        try {
            store.commit('GET_REMOVED_DUPLICATE_UNDO_STACK_DETAILS', ApiStatus.LOADING);
            const { data } = await citationApi.getUndoStackDetails({ projectId, screen: DuplicationUndoScreen.REMOVED_DUPLICATE });
            store.commit('GET_REMOVED_DUPLICATE_UNDO_STACK_DETAILS', { data });
        } catch (error) {
            store.commit('GET_REMOVED_DUPLICATE_UNDO_STACK_DETAILS', { error });
        }
    },
    async getManuallyRemovedDuplicateUndoStackDetails(store: Store<DuplicateCitationState>, projectId: number) {
        try {
            store.commit('GET_MANUALLY_REMOVED_DUPLICATE_UNDO_STACK_DETAILS', ApiStatus.LOADING);
            const { data } = await citationApi.getUndoStackDetails({ projectId, screen: DuplicationUndoScreen.MANUALLY_REMOVED_DUPLICATE });
            store.commit('GET_MANUALLY_REMOVED_DUPLICATE_UNDO_STACK_DETAILS', { data });
        } catch (error) {
            store.commit('GET_MANUALLY_REMOVED_DUPLICATE_UNDO_STACK_DETAILS', { error });
        }
    },
    async getPartialDuplicateUndoStackDetails(store: Store<DuplicateCitationState>, projectId: number) {
        try {
            store.commit('GET_PARTIAL_DUPLICATE_UNDO_STACK_DETAILS', ApiStatus.LOADING);
            const { data } = await citationApi.getUndoStackDetails({ projectId, screen: DuplicationUndoScreen.PARTIAL_DUPLICATE });
            store.commit('GET_PARTIAL_DUPLICATE_UNDO_STACK_DETAILS', { data });
        } catch (error) {
            store.commit('GET_PARTIAL_DUPLICATE_UNDO_STACK_DETAILS', { error });
        }
    },
    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Get V2 undo stack details for cluster-based undo (SYSTEM_DETECTED screen)
     */
    async getRemovedDuplicateUndoStackDetailsV2(store: Store<DuplicateCitationState>, projectId: number) {
        try {
            store.commit('GET_REMOVED_DUPLICATE_UNDO_STACK_DETAILS_V2', ApiStatus.LOADING);
            const { data } = await citationApi.getUndoStackDetailsV2({ projectId, screen: 'SYSTEM_DETECTED' });
            store.commit('GET_REMOVED_DUPLICATE_UNDO_STACK_DETAILS_V2', { data });
        } catch (error) {
            store.commit('GET_REMOVED_DUPLICATE_UNDO_STACK_DETAILS_V2', { error });
        }
    },
    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Get V2 undo stack details for cluster-based undo (MANUALLY_REMOVED screen)
     */
    async getManuallyRemovedDuplicateUndoStackDetailsV2(store: Store<DuplicateCitationState>, projectId: number) {
        try {
            store.commit('GET_MANUALLY_REMOVED_DUPLICATE_UNDO_STACK_DETAILS_V2', ApiStatus.LOADING);
            const { data } = await citationApi.getUndoStackDetailsV2({ projectId, screen: 'MANUALLY_REMOVED' });
            store.commit('GET_MANUALLY_REMOVED_DUPLICATE_UNDO_STACK_DETAILS_V2', { data });
        } catch (error) {
            store.commit('GET_MANUALLY_REMOVED_DUPLICATE_UNDO_STACK_DETAILS_V2', { error });
        }
    },
    async revertCitationDuplicationStatuses(store: Store<DuplicateCitationState>, payload: DuplicateUndoStackDetailsPayLoad) {
        try {
            store.commit('REVERT_CITATION_DUPLICATION_STATUSES', ApiStatus.LOADING);
            const { data } = await citationApi.revertCitationDuplicationStatuses(payload);
            store.commit('REVERT_CITATION_DUPLICATION_STATUSES', { data });
        } catch (error) {
            store.commit('REVERT_CITATION_DUPLICATION_STATUSES', { error });
        }
    },
    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Undo cluster-based "Mark as Not Duplicate" operation (V2)
     * Restores citations to their previous cluster state using snapshots
     */
    async revertCitationDuplicationStatusesV2(store: Store<DuplicateCitationState>, payload: { projectId: number; screen: 'SYSTEM_DETECTED' | 'MANUALLY_REMOVED' }) {
        try {
            store.commit('REVERT_CITATION_DUPLICATION_STATUSES_V2', ApiStatus.LOADING);
            const { data } = await citationApi.revertCitationDuplicationStatusesV2(payload);
            store.commit('REVERT_CITATION_DUPLICATION_STATUSES_V2', { data });
        } catch (error) {
            store.commit('REVERT_CITATION_DUPLICATION_STATUSES_V2', { error });
        }
    },
    async getScreeningCountByFile(store: Store<DuplicateCitationState>, payload: CitationScreeningCountPayload) {
        try {
            store.commit('GET_SCREENING_COUNT_BY_FILE', ApiStatus.LOADING);
            const { data } = await citationApi.getScreeningCountByFile(payload);
            store.commit('GET_SCREENING_COUNT_BY_FILE', { data })
        } catch (error) {
            store.commit('GET_SCREENING_COUNT_BY_FILE', { error })
        }
    },

    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Get cluster-based duplicate counts by file
     * Returns accurate counts using new cluster-based deduplication system
     */
    async getClusterBasedDuplicateCountsByFile(store: Store<DuplicateCitationState>, projectId: number) {
        try {
            store.commit('GET_CLUSTER_BASED_DUPLICATE_COUNTS_BY_FILE', ApiStatus.LOADING);
            const { data } = await citationApi.getClusterBasedDuplicateCountsByFile(projectId);
            store.commit('GET_CLUSTER_BASED_DUPLICATE_COUNTS_BY_FILE', { data });
        } catch (error) {
            store.commit('GET_CLUSTER_BASED_DUPLICATE_COUNTS_BY_FILE', { error });
        }
    },

    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Get undo stack details for possible duplicates removed from screening
     */
    async getPossibleDuplicatesUndoStackDetailsV2({ commit }: any, projectId: number) {
        try {
            commit('GET_POSSIBLE_DUPLICATES_UNDO_STACK_DETAILS_V2', ApiStatus.LOADING);
            const { data } = await citationApi.getUndoStackDetailsV2({ projectId, screen: 'POSSIBLE_DUPLICATES_REMOVED' });
            commit('GET_POSSIBLE_DUPLICATES_UNDO_STACK_DETAILS_V2', { data });
        } catch (error) {
            commit('GET_POSSIBLE_DUPLICATES_UNDO_STACK_DETAILS_V2', { error });
        }
    },

    // JIRA: SUMDEV-2964
    /**
     * JIRA: SUMDEV-2964
     * Undo possible duplicates removal from screening
     */
    async revertPossibleDuplicatesRemovalV2({ commit }: any, projectId: number) {
        try {
            commit('REVERT_POSSIBLE_DUPLICATES_REMOVAL_V2', ApiStatus.LOADING);
            const { data } = await citationApi.revertCitationDuplicationStatusesV2({ projectId, screen: 'POSSIBLE_DUPLICATES_REMOVED' });
            commit('REVERT_POSSIBLE_DUPLICATES_REMOVAL_V2', { data });
        } catch (error) {
            commit('REVERT_POSSIBLE_DUPLICATES_REMOVAL_V2', { error });
        }
    }
}



